import type {
  AgentOrchestratorStepResult,
  OrchestratedStepKind
} from '@axilon-apps/domains-ontology-ui-state'
import { artifactHref } from '@atbash-labs/plantview-links'

import { customAgentConfigHref } from '../../lib/component-links'
import type { ChatArtifactRef } from './chatArtifacts'

export type EntityLinkKind = 'notification_rule' | 'custom_agent_config' | 'chat_artifact'

export interface EntityLink {
  id: string
  label: string
  href: string
  kind: EntityLinkKind
}

const ROUTE_FOR: Record<EntityLinkKind, (id: string) => string> = {
  notification_rule: (id) => `/configure?type=notification-rules&id=${encodeURIComponent(id)}`,
  custom_agent_config: customAgentConfigHref,
  chat_artifact: artifactHref
}

export const LIST_ROUTE_FOR: Partial<Record<EntityLinkKind, string>> = {
  notification_rule: '/configure?type=notification-rules',
  custom_agent_config: '/configure?type=custom'
}

function shortId(id: string): string {
  return id.length <= 8 ? id : id.slice(0, 8)
}

function toLinks(kind: EntityLinkKind, ids: readonly string[]): EntityLink[] {
  const seen = new Set<string>()
  const out: EntityLink[] = []
  for (const id of ids) {
    const trimmed = id.trim()
    if (trimmed.length === 0) continue
    if (seen.has(trimmed)) continue
    seen.add(trimmed)
    out.push({ id: trimmed, label: shortId(trimmed), href: ROUTE_FOR[kind](trimmed), kind })
  }
  return out
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []
}

/**
 * Collect entity links from every step result in `stepResults` and bucket them
 * by `kind`, preserving first-seen order both across kinds and within each
 * kind's bucket. Used by the orchestrator's wrap-up Summary card to surface
 * everything created across the whole turn in one place.
 *
 * Each kind gets its own bucket so callers can render one `EntityLinksRow`
 * per kind — that keeps each row uniformly colored and lets the row's own
 * collapse threshold apply per-kind.
 */
export function aggregateStepEntityLinks(
  stepResults: Iterable<AgentOrchestratorStepResult>
): EntityLink[][] {
  const buckets = new Map<EntityLinkKind, EntityLink[]>()
  const seenIds = new Set<string>()
  for (const step of stepResults) {
    const links = extractStepEntityLinks(step.agent, step.rawResponse, step.completed)
    if (!Array.isArray(links)) continue
    for (const link of links) {
      const dedupeKey = `${link.kind}:${link.id}`
      if (seenIds.has(dedupeKey)) continue
      seenIds.add(dedupeKey)
      const bucket = buckets.get(link.kind)
      if (bucket) bucket.push(link)
      else buckets.set(link.kind, [link])
    }
  }
  return Array.from(buckets.values())
}

export function chatArtifactEntityLinks(refs: readonly ChatArtifactRef[]): EntityLink[] {
  return refs.map((ref) => ({
    id: ref.id,
    label: ref.title,
    href: artifactHref(ref.id),
    kind: 'chat_artifact'
  }))
}

export function extractStepEntityLinks(
  agent: OrchestratedStepKind,
  rawResponse: unknown,
  completed: boolean
): EntityLink[] {
  if (!completed) return []
  const record = asRecord(rawResponse)
  if (record === null) return []
  switch (agent) {
    case 'notification_rules_builder':
      return toLinks('notification_rule', asStringArray(record.affected_notification_rule_ids))
    case 'custom_agent_builder':
      return toLinks('custom_agent_config', asStringArray(record.affected_custom_agent_config_ids))
    case 'query_agent':
    case 'ingest_query_agent':
    case 'ingest_agent':
    case 'dashboard_builder':
    case 'documents_agent': // Retired agent, retained for persisted step labels.
    case 'integrations_agent':
    case 'text':
      return []
  }
}
